import { Position } from '../../../../models/api/vehicle-position.model';
import { AppSelector } from '../../../../core/state/app-selector';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnDestroy
} from '@angular/core';
import { ReportsService } from '../../../core/reports.service';
import { of, Subscription } from 'rxjs';
import { tap, catchError, take, map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { PermissionsService } from '../../../../core/permissions.service';
import { ReportOptionsService } from '../report-options.service';

@Component({
  selector: 'app-vehicle-selector',
  templateUrl: './vehicle-selector.component.html',
  styleUrls: ['./vehicle-selector.component.scss']
})
export class VehicleSelectorComponent implements OnInit, OnDestroy {
  vehicles: any;
  vehiclesOptions = [];
  selectedVehicle: any = {};
  vehiclesIsLoading = true;
  groupSubscription: Subscription;

  @Input() reportId: number;
  @Input() type: string;
  @Input() label = this.translateService.instant('reports.vehicle');
  @Input() allVehiclesText = this.translateService.instant(
    'reports.all-vehicles'
  );
  @Input() placeholder = this.translateService.instant(
    'reports.select-vehicle'
  );
  @Input() selectedVehicleId: number;
  @Input() allVehiclesOption: boolean;
  @Input() forkliftsSelector: boolean;
  @Input() disableFilterByType: boolean = false;
  @Input() dropdownClass: string = 'dropdown--gray';
  @Input() dropdownIconClass: string = 'fa fa-chevron-down';
  @Output() selectedVehicleIdChange = new EventEmitter<number>();

  constructor(
    private reportsService: ReportsService,
    private translateService: TranslateService,
    public permissionsService: PermissionsService,
    private reportOptionsService: ReportOptionsService,
    private appSelector: AppSelector
  ) {
  }

  ngOnInit() {
    this.loadVehicles();
  }

  private onVehicleGroupChange(): (value: any) => void {
    return (res) => {
      this.prepareGroupFilter(res);
      this.filterAndSelectVehicle();
    };
  }

  private onFilterTypeChange(): (value: any) => void {
    return (res) => {
      this.prepareTypeFilter(res);
      this.filterAndSelectVehicle();
    };
  }

  private filterAndSelectVehicle() {
    this.vehiclesIsLoading = true;
    this.vehiclesOptions = this.prepareVehiclesOptions();
    this.selectVehicle();
    this.reportOptionsService.vehicleName = this.selectedVehicle.label;
    this.vehiclesIsLoading = false;
  }

  private prepareGroupFilter(res: any) {
    if (typeof res === 'object') {
      this.reportsService.filterByGroupIsEnable = true;
      this.reportsService.filterGroupIdArray = res;
    } else {
      this.reportsService.filterByGroupIsEnable = false;
    }
  }

  private prepareTypeFilter(res: any) {
    if (res > 0) {
      this.reportsService.filterByTypeIsEnable = true;
      this.reportsService.filterTypeId = res;
    } else {
      this.reportsService.filterByTypeIsEnable = false;
    }
  }

  prepareVehiclesOptions(): any[] {
    if (this.vehicles) {
      const filteredVehicles = this.filterVehiclesList();
      if (this.allVehiclesOption && filteredVehicles.length > 1) {
        return [...this.getAllVehiclesOption(), ...filteredVehicles];
      }
      return filteredVehicles;
    } else {
      return [];
    }
  }

  getAllVehiclesOption(): any {
    return [
      {
        label: this.forkliftsSelector
          ? this.translateService.instant('reports.all-forklifts')
          : this.allVehiclesText,
        value: 0
      }
    ];
  }

  private filterVehiclesList(): any[] {
    return this.vehicles
      .filter(
        (vehicle) =>
          !this.reportsService.filterByGroupIsEnable ||
          this.filterByGroup(vehicle)
      )
      .filter(
        (vehicle) =>
          this.disableFilterByType ||
          !this.reportsService.filterByTypeIsEnable ||
          this.filterByType(vehicle)
      )
      .map((vehicle) => {
        return { label: vehicle.description, value: vehicle.id };
      });
  }

  filterByType(vehicle: any): boolean {
    return vehicle.typeId === this.reportsService.filterTypeId;
  }

  filterByGroup(vehicle: any): boolean {
    return this.reportsService.filterGroupIdArray.indexOf(vehicle.id) !== -1;
  }

  selectVehicle() {
    if (this.vehiclesOptions.length > 0) {
      this.appSelector
        .getSelectedVehicle()
        .pipe(
          take(1),
          map((position: Position) => {
            if (position) {
              return position.vehicle;
            }
          })
        )
        .subscribe((globalSelectedVehicleId) => {
          if (this.selectedVehicleId > 1) {
            this.selectVehicleById(this.selectedVehicleId);
          } else {
            if (globalSelectedVehicleId) {
              this.selectVehicleById(globalSelectedVehicleId);
            } else {
              this.selectedVehicle = this.vehiclesOptions[0];
            }
          }
          this.selectedVehicleId = this.selectedVehicle.value;
          this.selectedVehicleIdChange.emit(this.selectedVehicle.value);
        });
    } else {
      this.selectedVehicleId = -1;
      this.selectedVehicleIdChange.emit(-1);
    }
  }

  private selectVehicleById(vehicleId) {
    this.selectedVehicle = this.vehiclesOptions.find(
      (vehiclesOption) => vehiclesOption.value.toString() === vehicleId.toString()
    );
    if (!this.selectedVehicle) {
      this.selectedVehicle = this.vehiclesOptions[0];
    }
  }

  ngOnDestroy() {
    if (this.groupSubscription) {
      this.groupSubscription.unsubscribe();
    }
  }

  loadVehicles() {
    this.appSelector
      .getSortedPositions()
      .pipe(take(1))
      .subscribe((positions) => {
        this.reportsService
          .getVehicles(this.reportId)
          .pipe(
            tap((data: any[]) => {
              this.vehicles = positions
                .map((position) => {
                  return {
                    ...data.find((el) => el.id.toString() === position.vehicle.toString())
                  };
                })
                .filter((position) => position.typeId !== undefined);
              this.filterAndSelectVehicle();
              this.groupSubscription = this.reportsService.filterByVehicleGroupId.subscribe(
                this.onVehicleGroupChange()
              );

              this.reportsService.filterByVehicleTypeId.subscribe(
                this.onFilterTypeChange()
              );
              this.onSelectedVehicleIdChange();
            }),
            catchError((error, caught) => {
              this.vehiclesOptions = [];
              this.vehicles = [];
              this.vehiclesIsLoading = false;
              this.appSelector.getSelectedVehicle().subscribe(vehicle => {
                if (vehicle) {
                  this.vehiclesOptions.push({ label: vehicle.name, value: vehicle.vehicle });
                  this.selectedVehicle = this.vehiclesOptions[0];
                  this.selectedVehicleId = this.selectedVehicle.value;
                  this.selectedVehicleIdChange.emit(this.selectedVehicleId);
                } else {
                  this.selectedVehicleIdChange.emit(-1);
                }
              });
              return of();
            })
          )
          .subscribe();
      });
  }

  onSelectedVehicleIdChange() {
    if (this.selectedVehicle) {
      this.selectedVehicleIdChange.emit(this.selectedVehicle.value);
      this.reportOptionsService.vehicleName = this.selectedVehicle.label;
      // this.permissionsService
      //   .getArchivePermissions(this.reportId, this.selectedVehicle.value, -1)
      //   .subscribe((res) => {
      //     this.permissionsService.setArchivePermission.next(res);
      //   });
    } else {
      this.selectedVehicleIdChange.emit(-1);
      // this.permissionsService
      //   .getArchivePermissions(this.reportId, 0, -1)
      //   .subscribe((res) => {
      //     this.permissionsService.setArchivePermission.next(res);
      //   });
    }
  }
}
